import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Fragment } from 'react'
import { SkeletonText } from 'utoniq-ui'

import { RevenueLabel } from '@/components/organisms'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

import { Action, State } from './'

type RevenueHistoryListProps = {
  loading: State['loadingHistories']
  hasNextPage: State['hasNextPageHistories']
  onFetchMore: Action['onFetchMore']
  histories: State['histories']
}
export const RevenueHistoryListCell: Cell<RevenueHistoryListProps> = ({
  loading,
  hasNextPage,
  histories,
  onFetchMore,
}) => {
  const { t } = useTranslation('consoleRevenue')
  return (
    <CellFrame sx={{ px: 5, pb: 3 }}>
      {!histories?.edges.length && !loading ? (
        <Typography sx={{ py: 6, mt: 3 }} align="center" color="text.secondary">
          {t('noRevenueHistory')}
        </Typography>
      ) : (
        <TableContainer sx={{ mx: 'auto', pb: 5 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ minWidth: 104, py: 2, pl: 0 }}>
                  <Typography variant="subtitle1">
                    {t('dateAndTime')}
                  </Typography>
                </TableCell>
                {['contents', 'amountOfMoney', 'balance'].map(item => (
                  <TableCell
                    align="left"
                    sx={{ minWidth: 77, py: 2, pl: 0 }}
                    key={item}
                  >
                    <Typography variant="subtitle1">{t(`${item}`)}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableScrollFetcher
              loading={loading}
              hasNextPage={hasNextPage}
              onFetchMore={onFetchMore}
              component={TableRow}
              colSpan={4}
              loadingComponent={
                <>
                  {Array.from(Array(7).keys()).map(key => (
                    <TableRow key={key}>
                      <TableCell
                        component="th"
                        sx={{ pl: 0, py: 5 }}
                        scope="row"
                      >
                        <SkeletonText variant="small" width="100%" />
                      </TableCell>
                      <TableCell sx={{ pl: 0, py: 5 }} align="left">
                        <SkeletonText variant="medium" width="100%" />
                      </TableCell>
                      <TableCell sx={{ pl: 0, py: 5 }} align="left">
                        <SkeletonText variant="small" width="100%" />
                      </TableCell>
                      <TableCell sx={{ pl: 0, py: 5 }} align="left">
                        <SkeletonText variant="small" width="100%" />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              }
              nodes={histories?.edges.map(({ node }, index) => (
                <Fragment key={index}>
                  <TableCell component="th" sx={{ pl: 0, py: 5 }} scope="row">
                    <Typography variant="body2">
                      {node.formattedCreatedAt}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ pl: 0, py: 5 }} align="left">
                    <RevenueLabel type={node.type} />
                  </TableCell>
                  <TableCell sx={{ pl: 0, py: 5 }} align="left">
                    <Typography variant="body2">
                      ¥{node.formattedAmount}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ pl: 0, py: 5 }} align="left">
                    <Typography variant="body2">
                      ¥{node.formattedBalance}
                    </Typography>
                  </TableCell>
                </Fragment>
              ))}
            />
          </Table>
        </TableContainer>
      )}
    </CellFrame>
  )
}
